import _ from 'lodash';

// Helper function to return an object with all feature flags and config values for a company
// Called on login and auth refresh and accessible from Redux store
const companyFeatureFlags = (companyFeatures, authExtras = {}) => {
  if (!companyFeatures) return null;

  const {
    stripeAccountSetupComplete = null,
    stripeAccountId = null,
  } = authExtras;

  // Features::Search
  const searchFeature = companyFeatures && _.find(companyFeatures, { key: 'search' });
  const searchEnabled = searchFeature?.enabled;

  // Features::Review
  const reviewFeature = companyFeatures && _.find(companyFeatures, { key: 'review' });
  const reviewEnabled = reviewFeature?.enabled;

  // Features::Setting
  const settingsFeature = companyFeatures && _.find(companyFeatures, { key: 'setting' });
  const settingsEnabled = settingsFeature?.enabled;

  // Features::ReportsConfig
  const reportsFeature = companyFeatures && _.find(companyFeatures, { key: 'reports_config' });
  const reportsKeys = reportsFeature?.config
    && reportsFeature?.enabled
    && Object.keys(reportsFeature?.config);
  const filteredReportsKeys = reportsKeys
    && reportsKeys.filter((key) => !_.isEmpty(reportsFeature.config[key]));
  const reportsEnabled = reportsFeature?.enabled && filteredReportsKeys?.length > 0;

  // Features::Overview
  const overviewFeature = companyFeatures && _.find(companyFeatures, { key: 'overview_embed' });
  const overviewEnabled = overviewFeature?.enabled && overviewFeature?.config?.embedId !== null;

  // Features::ContentId
  const contentIdFeature = companyFeatures && _.find(companyFeatures, { key: 'content_id' });
  const referralEnabled = settingsEnabled && settingsFeature?.config?.referralEnabled === true;
  const referralBonusPercent = parseFloat(settingsFeature?.config?.referralBonusPercent ?? 5.0);
  const contentIdEnabled = contentIdFeature?.enabled;

  const hasImportEnabledAssetLabels = contentIdEnabled
    && (contentIdFeature?.config?.assetConfig || []).some(
      (assetConfig) => assetConfig?.connectionType === 'IMPORT',
    );
  const hasAllowlistedChannels = contentIdEnabled && (contentIdFeature?.allowlist || []).length > 0;
  const contentIdAssetUploadEnabled = contentIdEnabled && hasImportEnabledAssetLabels;

  // Features::Fingerprinting
  const fingerprintingFeature = companyFeatures && _.find(companyFeatures, { key: 'fingerprinting' });
  const fingerprintingEnabled = fingerprintingFeature?.enabled;

  // Features::Cyclops
  const cyclopsFeature = companyFeatures && _.find(companyFeatures, { key: 'cyclops' });
  const cyclopsEnabled = cyclopsFeature?.enabled;
  const isCyclopsDemo = cyclopsEnabled && cyclopsFeature?.config?.demoOnly;
  const cyclopsAnalyticsDisabled = isCyclopsDemo && cyclopsFeature?.config?.disableAnalytics;
  const cyclopsSettingsDisabled = isCyclopsDemo && cyclopsFeature?.config?.disableSettings;

  // Additional feature flags derived from config
  const teamsSettingsEnabled = Boolean(
    searchFeature?.config?.enabledSearchSubtypes?.includes('Sports'),
  );
  const unitTerm = searchFeature?.config?.unitTerm;
  const unitsSettingsEnabled = Boolean(unitTerm);
  const allowSettings = settingsEnabled
    || teamsSettingsEnabled
    || unitsSettingsEnabled
    || Boolean(fingerprintingEnabled);
  const liveTrackerEnabled = settingsEnabled && settingsFeature?.config?.liveTrackerAnalyticsAccess;

  // In the case of a company with no features, they will be redirected to the Account page
  // with a message to contact support
  const unconfiguredCompany = !reviewEnabled
    && !searchEnabled
    && !overviewEnabled
    && !reportsEnabled
    && !cyclopsEnabled
    && !contentIdEnabled;

  const companyFlags = {
    fingerprintingConfig: fingerprintingFeature?.config,
    fingerprintingEnabled: Boolean(fingerprintingEnabled),
    searchConfig: searchFeature?.config,
    searchEnabled: Boolean(searchEnabled),
    reviewConfig: reviewFeature?.config,
    reviewEnabled: Boolean(reviewEnabled),
    settingsConfig: settingsFeature?.config,
    settingsEnabled: Boolean(settingsEnabled),
    liveTrackerEnabled: Boolean(liveTrackerEnabled),
    reportsConfig: reportsFeature?.config,
    reportsEnabled: Boolean(reportsEnabled),
    overviewConfig: overviewFeature?.config,
    overviewEnabled: Boolean(overviewEnabled),
    contentIdConfig: contentIdFeature?.config,
    contentIdEnabled: Boolean(contentIdEnabled),
    contentIdAssetUploadEnabled: Boolean(contentIdAssetUploadEnabled),
    hasAllowlistedChannels: Boolean(hasAllowlistedChannels),
    cyclopsConfig: cyclopsFeature?.config,
    cyclopsEnabled: Boolean(cyclopsEnabled),
    isCyclopsDemo: Boolean(isCyclopsDemo),
    cyclopsAnalyticsDisabled: Boolean(cyclopsAnalyticsDisabled),
    cyclopsSettingsDisabled: Boolean(cyclopsSettingsDisabled),
    allowSettings: Boolean(allowSettings),
    unconfiguredCompany: Boolean(unconfiguredCompany),
    stripeAccountSetupComplete: Boolean(stripeAccountSetupComplete),
    stripeAccountId,
    stripePayoutNeedsSetup: Boolean(contentIdEnabled && stripeAccountSetupComplete !== true),
    referralEnabled: Boolean(referralEnabled),
    referralBonusPercent,
  };

  return companyFlags;
};

export default companyFeatureFlags;
